/**
 * EmailCampaignReport — Relatório de uma campanha de e-mail.
 * Mostra KPIs cumulativos (delivered/opened/clicked/bounced/unsubscribed) e
 * permite drill-down nos destinatários filtrando por engajamento.
 *
 * Polling a cada 3s enquanto a campanha está em status='sending'.
 */
import { useState } from "react";
import { useLocation, useRoute } from "wouter";
import { trpc } from "@/lib/trpc";
import DashboardLayout from "@/components/DashboardLayout";
import { PermissionGuard } from "@/components/PermissionGuard";
import { usePermissions } from "@/hooks/usePermissions";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import { Separator } from "@/components/ui/separator";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Empty, EmptyHeader, EmptyMedia, EmptyTitle, EmptyDescription } from "@/components/ui/empty";
import {
  ArrowLeft, ChevronLeft, ChevronRight, Copy, Eye, Search, Inbox,
  Users, Send, CheckCheck, MousePointerClick, XCircle, Ban, Clock, Check,
  RefreshCw, StopCircle, AlertTriangle, FileDown,
} from "lucide-react";
import { downloadCsv } from "@/lib/download";
import { toast } from "sonner";
import { toUserFriendlyError } from "@/lib/error-messages";
import { MessageDetailDialog } from "@/components/email/MessageDetailDialog";

type RecipientFilter = "all" | "sent" | "delivered" | "opened" | "clicked" | "not_opened" | "bounced" | "unsubscribed" | "pending" | "failed";

const STATUS_CONFIG: Record<string, { label: string; variant: "default" | "secondary" | "destructive" | "outline" }> = {
  draft: { label: "Rascunho", variant: "secondary" },
  scheduled: { label: "Agendada", variant: "secondary" },
  sending: { label: "Em envio", variant: "secondary" },
  sent: { label: "Enviada", variant: "default" },
  error: { label: "Com erro", variant: "destructive" },
  cancelled: { label: "Cancelada", variant: "outline" },
};

// Configuração visual de cada status de mensagem (espelha CampaignReport
// do WhatsApp pra manter coerência visual entre os dois canais).
const MSG_STATUS_CONFIG: Record<string, { label: string; color: string; icon: React.ReactNode }> = {
  pending:   { label: "Pendente",  color: "bg-yellow-100 text-yellow-800",   icon: <Clock className="w-3 h-3" /> },
  sent:      { label: "Enviado",   color: "bg-blue-100 text-blue-800",       icon: <Check className="w-3 h-3" /> },
  delivered: { label: "Entregue",  color: "bg-green-100 text-green-800",     icon: <CheckCheck className="w-3 h-3" /> },
  opened:    { label: "Aberto",    color: "bg-purple-100 text-purple-800",   icon: <Eye className="w-3 h-3" /> },
  clicked:   { label: "Clicado",   color: "bg-indigo-100 text-indigo-800",   icon: <MousePointerClick className="w-3 h-3" /> },
  bounced:   { label: "Rejeitado", color: "bg-rose-100 text-rose-800",       icon: <Ban className="w-3 h-3" /> },
  failed:    { label: "Falhou",    color: "bg-red-100 text-red-800",         icon: <XCircle className="w-3 h-3" /> },
};

const PAGE_SIZE_OPTIONS = [20, 50, 100, 200] as const;
const DEFAULT_PAGE_SIZE = 50;

export default function EmailCampaignReport() {
  const [, navigate] = useLocation();
  const [, params] = useRoute("/emails/campaigns/:id");
  const id = params?.id ? Number(params.id) : 0;
  // O procedure de export exige email:export_reports (além da view que guarda
  // a página). Sem checar aqui, papel custom sem a permissão via o botão e
  // ganhava um toast de FORBIDDEN no clique.
  const { can } = usePermissions();
  const canExport = can("email:export_reports");

  const [filter, setFilter] = useState<RecipientFilter>("all");
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState<number>(DEFAULT_PAGE_SIZE);
  const [detailMessageId, setDetailMessageId] = useState<number | null>(null);

  // Auto-refresh: durante 1h após sentAt, os webhooks de open/click do SendGrid
  // ainda chegam em rajadas (a maioria dos opens acontece nos primeiros 30min).
  // Pollar a query principal a cada 60s nesse intervalo evita que o KPI
  // "Aberturas" mostre 0 só porque o cache do React Query é stale. Depois de 1h
  // o volume de eventos novos cai drasticamente — desliga o polling pra não
  // bater no DB sem necessidade. O botão "Atualizar" manual continua disponível.
  const REFRESH_WINDOW_MS = 60 * 60 * 1000;
  const REFRESH_INTERVAL_MS = 60 * 1000;
  const shouldAutoRefresh = (sentAtStr: string | null | undefined): boolean => {
    if (!sentAtStr) return false;
    const sentAt = new Date(sentAtStr).getTime();
    if (Number.isNaN(sentAt)) return false;
    return Date.now() - sentAt < REFRESH_WINDOW_MS;
  };

  const utils = trpc.useUtils();
  const [isRefreshing, setIsRefreshing] = useState(false);
  // Invalida as três queries que essa página consome — `get` (campanha + KPIs),
  // `getRecipients` (tabela de destinatários) e `getProgress` (barra durante
  // envio). Sem passar input pra cobrir TODAS as variantes (a queryKey de
  // `getRecipients` inclui filter/search/limit/offset, então um invalidate
  // com { id } parcial não matcha — invalidate sem input matcha por procedure).
  // Aguarda os refetches resolverem pra desabilitar o botão durante o ciclo
  // (UX: dá feedback visual e impede cliques duplos).
  const handleRefresh = async () => {
    if (isRefreshing) return;
    setIsRefreshing(true);
    try {
      await Promise.all([
        utils.emailCampaigns.get.invalidate(),
        utils.emailCampaigns.getRecipients.invalidate(),
        utils.emailCampaigns.getProgress.invalidate(),
      ]);
    } finally {
      setIsRefreshing(false);
    }
  };

  const { data: campaign, isLoading: loadingCampaign } = trpc.emailCampaigns.get.useQuery(
    { id },
    {
      enabled: id > 0,
      refetchInterval: (q) => {
        const sentAt = (q.state.data as any)?.sentAt;
        return shouldAutoRefresh(sentAt) ? REFRESH_INTERVAL_MS : false;
      },
    },
  );

  // Sender configurado a nível de tenant (Settings > SendGrid). Usado como
  // fallback quando a campanha não gravou senderName/senderEmail próprios —
  // espelha a resolução que sendEmailBatch faz em runtime.
  const { data: tenantSender } = trpc.emailCampaigns.getSender.useQuery();

  // Ranking de URLs clicadas — útil pra campanhas com múltiplos CTAs (qual
  // botão converteu mais). Só faz request quando a campanha já foi enviada
  // (sentAt setado), evita query pesada em rascunhos.
  const { data: clickedLinks } = trpc.emailCampaigns.getClickedLinks.useQuery(
    { id, limit: 10 },
    {
      enabled: id > 0 && !!(campaign as any)?.sentAt,
      refetchInterval: (q) => {
        const sentAt = (campaign as any)?.sentAt;
        return shouldAutoRefresh(sentAt) ? REFRESH_INTERVAL_MS : false;
      },
    },
  );

  const { data: progress } = trpc.emailCampaigns.getProgress.useQuery(
    { id },
    {
      enabled: id > 0,
      refetchInterval: (q) => {
        const status = (q.state.data as any)?.status;
        return status === "sending" ? 3000 : false;
      },
    },
  );

  // sentAt vem da query `campaign` que já tem polling. Lemos do queryClient
  // pra evitar depender da closure de render (o `campaign` da closure pode
  // ficar desatualizado quando o usuário troca de filtro/página).
  const recipientsQueryKey = { id, filter, search: search || undefined, limit: pageSize, offset: page * pageSize };
  const { data: recipientsData, isLoading: loadingRecipients } = trpc.emailCampaigns.getRecipients.useQuery(
    recipientsQueryKey,
    {
      enabled: id > 0,
      refetchInterval: () => {
        // Espelha o polling do `campaign` acima — durante 1h após envio, mantém
        // a lista de destinatários (status, openedAt, clickedAt) sincronizada.
        const campaignData = utils.emailCampaigns.get.getData({ id }) as { sentAt?: string | null } | undefined;
        return shouldAutoRefresh(campaignData?.sentAt) ? REFRESH_INTERVAL_MS : false;
      },
    },
  );

  /**
   * Export do log por destinatário. Manda o MESMO filtro e busca da tabela —
   * quem está olhando "Abriu" espera baixar quem abriu, não a campanha toda.
   */
  const exportMutation = trpc.emailCampaigns.exportReport.useMutation({
    onSuccess: (res) => {
      downloadCsv(res.csv, res.fileName);
      toast.success(
        res.truncated
          ? `${res.total.toLocaleString("pt-BR")} linhas exportadas — o arquivo foi cortado no teto de linhas.`
          : `${res.total.toLocaleString("pt-BR")} destinatário(s) exportado(s).`,
      );
    },
    onError: (err) => toast.error(toUserFriendlyError(err, "Erro ao exportar relatório")),
  });

  const duplicateMutation = trpc.emailCampaigns.duplicate.useMutation({
    onSuccess: (data: any) => {
      if (data?.id) {
        toast.success("Campanha duplicada como rascunho.");
        navigate(`/emails/${data.id}/editor`);
      }
    },
    onError: (err) => toast.error(toUserFriendlyError(err, "Erro ao duplicar")),
  });

  // Parar envio. O backend só grava o status; quem interrompe de fato é o
  // loop de envio, que consulta esse status entre os lotes — por isso a
  // mensagem fala em "até 30 segundos" em vez de prometer parada imediata.
  const cancelMutation = trpc.emailCampaigns.cancel.useMutation({
    onSuccess: (data: any) => {
      toast.success(
        data?.cancelledMessages > 0
          ? `Envio interrompido. ${data.cancelledMessages.toLocaleString("pt-BR")} destinatário(s) não vão receber.`
          : "Envio interrompido.",
        { description: "Os disparos já em andamento param em até 30 segundos." },
      );
      utils.emailCampaigns.get.invalidate({ id });
      utils.emailCampaigns.getProgress.invalidate({ id });
      utils.emailCampaigns.getRecipients.invalidate();
    },
    onError: (err) => toast.error(toUserFriendlyError(err, "Erro ao parar o envio")),
  });

  if (id <= 0) {
    return (
      <DashboardLayout>
        <div className="p-6">
          <p className="text-sm text-muted-foreground">ID de campanha inválido.</p>
        </div>
      </DashboardLayout>
    );
  }

  if (loadingCampaign) {
    return (
      <DashboardLayout>
        <div className="p-6">
          <p className="text-sm text-muted-foreground">Carregando campanha…</p>
        </div>
      </DashboardLayout>
    );
  }

  if (!campaign) {
    return (
      <DashboardLayout>
        <div className="p-6">
          <Empty className="py-16">
            <EmptyHeader>
              <EmptyMedia variant="icon"><Inbox className="h-5 w-5" /></EmptyMedia>
              <EmptyTitle>Campanha não encontrada</EmptyTitle>
              <EmptyDescription>Esta campanha pode ter sido excluída ou pertence a outra conta.</EmptyDescription>
            </EmptyHeader>
            <Button variant="outline" onClick={() => navigate("/emails")} className="gap-2">
              <ArrowLeft className="h-4 w-4" /> Voltar
            </Button>
          </Empty>
        </div>
      </DashboardLayout>
    );
  }

  const status = (progress?.status ?? campaign.status) as string;
  const statusCfg = STATUS_CONFIG[status] ?? { label: status, variant: "outline" as const };
  const delivered = progress?.statsDelivered ?? campaign.statsDelivered ?? 0;
  const opened = progress?.statsOpened ?? campaign.statsOpened ?? 0;
  // Opens automáticos (Apple MPP / Gmail image proxy) — sg_machine_open=true
  // no payload do SendGrid. Mostrados separadamente pra não inflar o KPI
  // principal de engajamento real. Campanhas antigas (pré-migration 0060)
  // têm o campo null/0 — fallback pra 0 mantém compatibilidade.
  const opensMachine = (progress as any)?.statsOpensMachine ?? (campaign as any)?.statsOpensMachine ?? 0;
  const clicked = progress?.statsClicked ?? campaign.statsClicked ?? 0;
  const bounced = progress?.statsBounced ?? campaign.statsBounced ?? 0;
  const unsubscribed = progress?.statsUnsubscribed ?? campaign.statsUnsubscribed ?? 0;
  const sentSoFar = progress?.sentSoFar ?? 0;
  // Registrados e nunca enviados. Antes ficavam invisíveis: entravam no Total
  // e no "Enviados", como se tivessem saído.
  const pendingCount = (progress as any)?.pending ?? 0;
  const failedCount = (progress as any)?.failed ?? 0;
  const canCancel = status === "sending" || status === "scheduled";
  // Total = totalRecipients gravado na campanha. Como campanhas antigas
  // (pré-PR de progresso) não preencheram esse campo, usamos como fallback
  // a maior contagem disponível (sentSoFar ou soma das estatísticas) pra
  // não mostrar Total=0 com Entregues=3656.
  const totalRaw = progress?.totalRecipients ?? campaign.totalRecipients ?? 0;
  // `totalMessages` são as linhas registradas para a campanha — inclui quem
  // não recebeu. É o denominador honesto: Total 29.731 = 15.178 enviados +
  // 14.553 que ficaram pelo caminho. Usar o total de enviados aqui esconderia
  // justamente a diferença que o aviso acima existe para mostrar.
  const totalMessages = (progress as any)?.totalMessages ?? 0;
  const total = Math.max(totalRaw, totalMessages, sentSoFar, delivered + bounced + unsubscribed);
  const openRate = delivered > 0 ? Math.round((opened / delivered) * 100) : 0;
  const clickRate = opened > 0 ? Math.round((clicked / opened) * 100) : 0;

  // Resolução do remetente espelha o backend (sendEmailBatch): campanha tem
  // precedência; se vazio, usa sender do tenant. Antes, campanhas que não
  // gravaram senderName/senderEmail mostravam "—" mesmo com sender ok.
  const displaySenderName = (campaign.senderName ?? "").trim() || (tenantSender?.senderName ?? "").trim();
  const displaySenderEmail = (campaign.senderEmail ?? "").trim() || (tenantSender?.senderEmail ?? "").trim();

  const segCount = Array.isArray(campaign.segmentIds) ? campaign.segmentIds.length : 0;
  const tagCount = Array.isArray(campaign.tagIds) ? campaign.tagIds.length : 0;

  const recipients = (recipientsData as any)?.rows ?? [];
  const recipientsTotal = (recipientsData as any)?.total ?? 0;

  const handleFilterChange = (next: string) => {
    setFilter(next as RecipientFilter);
    setPage(0);
  };

  return (
    <PermissionGuard permission="email:view_campaigns">
    <DashboardLayout>
      <div className="space-y-6 p-6">
        {/* Header */}
        <div className="flex items-start justify-between gap-3">
          <div className="flex items-start gap-3 min-w-0">
            <Button variant="outline" size="icon" onClick={() => navigate("/emails")} title="Voltar">
              <ArrowLeft className="h-4 w-4" />
            </Button>
            <div className="min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <h1 className="text-2xl font-bold truncate">{campaign.name}</h1>
                <Badge variant={statusCfg.variant}>{statusCfg.label}</Badge>
              </div>
              <p className="text-sm text-muted-foreground mt-1 truncate">{campaign.subject || "Sem assunto"}</p>
            </div>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <Button
              variant="outline"
              className="gap-2"
              onClick={handleRefresh}
              disabled={isRefreshing}
              title="Atualizar dados"
            >
              <RefreshCw className={`h-4 w-4 ${isRefreshing ? "animate-spin" : ""}`} />
              {isRefreshing ? "Atualizando..." : "Atualizar"}
            </Button>
            {canCancel && (
              <Button
                variant="destructive"
                className="gap-2"
                onClick={() => {
                  if (confirm("Parar o envio desta campanha? Quem ainda não recebeu não vai receber, e não dá para retomar de onde parou.")) {
                    cancelMutation.mutate({ id });
                  }
                }}
                disabled={cancelMutation.isPending}
                title="Interromper o envio em andamento"
              >
                <StopCircle className="h-4 w-4" />
                {cancelMutation.isPending ? "Parando..." : "Parar envio"}
              </Button>
            )}
            {canExport && (
              <Button
                variant="outline"
                className="gap-2"
                onClick={() => exportMutation.mutate({ id, filter, search: search || undefined })}
                disabled={exportMutation.isPending || recipientsTotal === 0}
                title="Baixa o log completo do filtro atual em CSV"
              >
                <FileDown className="h-4 w-4" />
                {exportMutation.isPending ? "Gerando..." : "Exportar CSV"}
              </Button>
            )}
            <Button
              variant="outline"
              className="gap-2"
              onClick={() => duplicateMutation.mutate({ id })}
              disabled={duplicateMutation.isPending}
            >
              <Copy className="h-4 w-4" />
              Duplicar
            </Button>
          </div>
        </div>

        {/* Info da campanha */}
        <Card className="p-4">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
            <div>
              <div className="text-xs text-muted-foreground">Remetente</div>
              <div className="mt-0.5 truncate">{displaySenderName || "—"}</div>
              <div className="text-xs text-muted-foreground truncate">{displaySenderEmail || ""}</div>
            </div>
            <div>
              <div className="text-xs text-muted-foreground">Público</div>
              <div className="mt-0.5">
                {segCount + tagCount === 0
                  ? "—"
                  : `${segCount} segmento${segCount !== 1 ? "s" : ""} · ${tagCount} tag${tagCount !== 1 ? "s" : ""}`}
              </div>
            </div>
            <div>
              <div className="text-xs text-muted-foreground">Enviado em</div>
              <div className="mt-0.5">
                {(() => {
                  // Fallback: se a campanha ainda não gravou sentAt (worker em
                  // andamento ou crash antes do update final), mostra o
                  // timestamp mais recente das mensagens. Se ambos forem null
                  // e o status é 'sending', mostra "Em processamento".
                  const ts = campaign.sentAt ?? (progress as any)?.lastSentAt ?? null;
                  if (ts) return new Date(ts).toLocaleString("pt-BR");
                  if (status === "sending") return "Em processamento";
                  return "—";
                })()}
              </div>
            </div>
            <div>
              <div className="text-xs text-muted-foreground">Destinatários</div>
              <div className="mt-0.5 font-medium">{total}</div>
            </div>
          </div>

          {/* Barra de progresso quando enviando */}
          {status === "sending" && total > 0 && (
            <>
              <Separator className="my-4" />
              <div>
                <div className="flex items-center justify-between text-sm mb-2">
                  <span className="font-medium">Enviando…</span>
                  <span className="text-muted-foreground">{sentSoFar} de {total}</span>
                </div>
                <Progress value={Math.min(100, Math.round((sentSoFar / total) * 100))} className="h-2" />
              </div>
            </>
          )}
        </Card>

        {/* Não-enviados: o número que ficava escondido. Uma campanha pode
            terminar com linhas em 'pending' (envio interrompido, container
            reiniciado no meio) e nada na tela dizia isso — o Total contava
            todas como se tivessem saído.

            Era um card âmbar de cinco linhas com dois botões, ocupando mais
            espaço vertical que os próprios KPIs. Para uma campanha grande, 120
            falhas em 9.939 é rodapé, não manchete: virou uma linha discreta,
            com os números como links para o mesmo filtro que os botões
            aplicavam. Continua visível — só parou de gritar. */}
        {status !== "sending" && (pendingCount > 0 || failedCount > 0) && (
          <div className="flex flex-wrap items-center gap-x-1.5 gap-y-1 text-sm text-muted-foreground">
            <AlertTriangle className="h-4 w-4 text-amber-600 shrink-0" />
            <span>
              {(pendingCount + failedCount).toLocaleString("pt-BR")} não receberam:
            </span>
            {pendingCount > 0 && (
              <button
                type="button"
                onClick={() => handleFilterChange("pending")}
                className="underline underline-offset-2 hover:text-foreground"
              >
                {pendingCount.toLocaleString("pt-BR")} em espera
              </button>
            )}
            {pendingCount > 0 && failedCount > 0 && <span>·</span>}
            {failedCount > 0 && (
              <button
                type="button"
                onClick={() => handleFilterChange("failed")}
                className="underline underline-offset-2 hover:text-foreground"
              >
                {failedCount.toLocaleString("pt-BR")} falharam
              </button>
            )}
          </div>
        )}

        {/* KPIs — cards clicáveis funcionam como atalhos de filtro: clicar
            num card aplica o filtro correspondente na tabela "Detalhamento por
            destinatário" abaixo. O card ativo recebe ring colorido. */}
        <div className="grid grid-cols-2 md:grid-cols-6 gap-4">
          {(() => {
            const KPI_CARDS = [
              { key: "all" as const, label: "Total", value: total, icon: <Users className="w-4 h-4 text-muted-foreground" />, valueClass: "", ringClass: "ring-2 ring-foreground/40" },
              // `sentSoFar` conta as mensagens com data de envio — o que
              // realmente saiu. Campanha interrompida ou com linhas presas em
              // espera mostra aqui menos que o Total, e é essa diferença que o
              // aviso logo acima explica.
              { key: "sent" as const, label: "Enviados", value: sentSoFar > 0 ? sentSoFar : (delivered + bounced), icon: <Send className="w-4 h-4 text-blue-500" />, valueClass: "text-blue-600", ringClass: "ring-2 ring-blue-400" },
              { key: "delivered" as const, label: "Entregues", value: delivered, icon: <CheckCheck className="w-4 h-4 text-green-500" />, valueClass: "text-green-600", ringClass: "ring-2 ring-green-400", percent: total > 0 ? `${Math.round((delivered / total) * 100)}%` : undefined },
              { key: "opened" as const, label: "Aberturas", value: opened, icon: <Eye className="w-4 h-4 text-purple-500" />, valueClass: "text-purple-600", ringClass: "ring-2 ring-purple-400", percent: delivered > 0 ? `${openRate}%` : undefined, footer: opensMachine > 0 ? `+${opensMachine} automáticas (Apple/Gmail)` : undefined },
              { key: "clicked" as const, label: "Cliques", value: clicked, icon: <MousePointerClick className="w-4 h-4 text-indigo-500" />, valueClass: "text-indigo-600", ringClass: "ring-2 ring-indigo-400", percent: opened > 0 ? `${clickRate}%` : undefined },
              { key: "bounced" as const, label: "Bounces", value: bounced, icon: <Ban className="w-4 h-4 text-rose-500" />, valueClass: "text-rose-600", ringClass: "ring-2 ring-rose-400", footer: unsubscribed > 0 ? `+${unsubscribed} descadastros` : undefined },
            ];
            return KPI_CARDS.map((c) => (
              <Card
                key={c.key}
                role="button"
                tabIndex={0}
                aria-pressed={filter === c.key}
                onClick={() => handleFilterChange(c.key)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    handleFilterChange(c.key);
                  }
                }}
                className={`cursor-pointer transition-all hover:shadow-md ${filter === c.key ? c.ringClass : "hover:ring-1 hover:ring-foreground/20"}`}
              >
                <CardContent className="pt-4 pb-4">
                  <div className="flex items-center gap-2">
                    {c.icon}
                    <span className="text-sm text-muted-foreground">{c.label}</span>
                  </div>
                  <p className={`text-2xl font-bold mt-1 ${c.valueClass}`}>{c.value}</p>
                  {c.percent && <p className="text-xs text-muted-foreground">{c.percent}</p>}
                  {c.footer && <p className="text-xs text-muted-foreground">{c.footer}</p>}
                </CardContent>
              </Card>
            ));
          })()}
        </div>

        {/* Ponte para a higiene. O relatório mostra o bounce DESTA campanha; a
            pergunta seguinte ("e os mesmos endereços que quebram toda edição?")
            só se responde na base inteira. Sem este link, o usuário via 372
            bounces e não tinha para onde ir. */}
        {bounced > 0 && (
          <button
            type="button"
            onClick={() => navigate("/emails/higiene")}
            className="text-sm text-muted-foreground underline underline-offset-2 hover:text-foreground text-left"
          >
            Ver todos os endereços que não recebem mais e limpar a base →
          </button>
        )}

        {/* Ranking de URLs clicadas — só aparece quando há campanha enviada e
            pelo menos 1 click registrado. Pra campanhas com 1 único CTA o card
            ainda é útil (confirma qual link de fato gerou tráfego); pra
            campanhas com múltiplos botões mostra qual converteu mais. */}
        {clickedLinks && clickedLinks.length > 0 && (
          <Card>
            <div className="p-4 border-b flex items-center gap-2">
              <MousePointerClick className="w-4 h-4 text-indigo-500" />
              <h2 className="text-base font-semibold">Links mais clicados</h2>
              <span className="text-xs text-muted-foreground">
                — ordenado por destinatários únicos que clicaram
              </span>
            </div>
            <CardContent className="p-0">
              <table className="w-full text-sm">
                <thead className="bg-muted/40 text-xs uppercase text-muted-foreground">
                  <tr>
                    <th className="px-4 py-2 text-left font-medium">URL</th>
                    <th className="px-4 py-2 text-right font-medium">Cliques únicos</th>
                    <th className="px-4 py-2 text-right font-medium">Total de cliques</th>
                  </tr>
                </thead>
                <tbody>
                  {clickedLinks.map((link, i) => {
                    // link.url vem de email_events.metadata->>'url' (preenchido
                    // pelo webhook do SendGrid). Campanhas históricas podem ter
                    // URLs javascript:/data:/etc — anteriores à validação no
                    // envio (findInvalidLinks). Renderizar como href permitiria
                    // XSS via href clicável. Só consideramos clicável se for
                    // http(s); senão mostramos como texto bruto.
                    const isClickable = /^https?:\/\//i.test(link.url ?? "");
                    return (
                      <tr key={i} className="border-t hover:bg-muted/30">
                        <td className="px-4 py-2 truncate max-w-md" title={link.url}>
                          {isClickable ? (
                            <a href={link.url} target="_blank" rel="noopener noreferrer" className="text-indigo-600 hover:underline">
                              {link.url}
                            </a>
                          ) : (
                            <span className="text-muted-foreground" title="Protocolo não-HTTP — não clicável por segurança">
                              {link.url || "(URL vazia)"}
                            </span>
                          )}
                        </td>
                        <td className="px-4 py-2 text-right font-mono font-medium">{link.uniqueClicks}</td>
                        <td className="px-4 py-2 text-right font-mono text-muted-foreground">{link.totalClicks}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </CardContent>
          </Card>
        )}

        {/* Tabela de destinatários: filtro dropdown + busca, paginação 50/pg */}
        <Card>
          <div className="p-4 border-b flex flex-wrap items-center gap-3 justify-between">
            <h2 className="text-base font-semibold">Detalhamento por destinatário</h2>
            <div className="flex items-center gap-2 flex-wrap">
              <div className="relative w-full sm:w-64">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Buscar e-mail…"
                  value={search}
                  onChange={(e) => { setSearch(e.target.value); setPage(0); }}
                  className="pl-9 h-9"
                />
              </div>
              <Select value={filter} onValueChange={handleFilterChange}>
                <SelectTrigger className="w-48 h-9">
                  <SelectValue placeholder="Filtrar por status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todos os status</SelectItem>
                  <SelectItem value="sent">Enviados</SelectItem>
                  <SelectItem value="delivered">Entregues</SelectItem>
                  <SelectItem value="opened">Aberturas</SelectItem>
                  <SelectItem value="clicked">Cliques</SelectItem>
                  <SelectItem value="not_opened">Não abriram</SelectItem>
                  <SelectItem value="bounced">Bounces</SelectItem>
                  <SelectItem value="unsubscribed">Descadastros</SelectItem>
                  <SelectItem value="pending">Não enviados (em espera)</SelectItem>
                  <SelectItem value="failed">Falhas de envio</SelectItem>
                </SelectContent>
              </Select>
              <Select
                value={String(pageSize)}
                onValueChange={(v) => {
                  setPageSize(Number(v));
                  setPage(0);
                }}
              >
                <SelectTrigger className="w-32 h-9" aria-label="Itens por página">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {PAGE_SIZE_OPTIONS.map((n) => (
                    <SelectItem key={n} value={String(n)}>{n}/página</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          {loadingRecipients ? (
            <p className="text-sm text-muted-foreground text-center py-12">Carregando…</p>
          ) : recipients.length === 0 ? (
            <Empty className="py-12">
              <EmptyHeader>
                <EmptyMedia variant="icon"><Inbox className="h-5 w-5" /></EmptyMedia>
                <EmptyTitle>Nenhum destinatário neste filtro</EmptyTitle>
                <EmptyDescription>
                  {filter === "unsubscribed"
                    ? "Nenhum descadastro registrado ainda."
                    : "Sem registros neste critério."}
                </EmptyDescription>
              </EmptyHeader>
            </Empty>
          ) : (
            <>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b text-muted-foreground">
                      <th className="text-left py-3 px-3">Contato</th>
                      <th className="text-left py-3 px-3">Status</th>
                      <th className="text-left py-3 px-3">Entregue em</th>
                      <th className="text-left py-3 px-3">Aberto em</th>
                      <th className="text-left py-3 px-3">Clicado em</th>
                      <th className="text-right py-3 px-3 w-10"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {recipients.map((r: any) => {
                      const cfg = MSG_STATUS_CONFIG[r.status] ?? MSG_STATUS_CONFIG.pending;
                      return (
                        <tr key={r.id} className="border-b last:border-0 hover:bg-muted/30 transition-colors">
                          <td className="py-3 px-3">
                            <div className="font-medium truncate max-w-[240px]">{r.contactName || "—"}</div>
                            <div className="font-mono text-xs text-muted-foreground truncate max-w-[240px]">{r.to}</div>
                          </td>
                          <td className="py-3 px-3">
                            <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${cfg.color}`}>
                              {cfg.icon}
                              {cfg.label}
                            </span>
                          </td>
                          <td className="py-3 px-3 text-xs text-muted-foreground whitespace-nowrap">
                            {r.deliveredAt ? new Date(r.deliveredAt).toLocaleString("pt-BR") : "—"}
                          </td>
                          <td className="py-3 px-3 text-xs text-muted-foreground whitespace-nowrap">
                            {r.openedAt ? new Date(r.openedAt).toLocaleString("pt-BR") : "—"}
                          </td>
                          <td className="py-3 px-3 text-xs text-muted-foreground whitespace-nowrap">
                            {r.clickedAt ? new Date(r.clickedAt).toLocaleString("pt-BR") : "—"}
                          </td>
                          <td className="py-3 px-3 text-right">
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-8 w-8"
                              onClick={() => setDetailMessageId(r.id)}
                              title="Ver detalhes"
                            >
                              <Eye className="h-4 w-4" />
                            </Button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              <div className="flex items-center justify-between px-4 py-3 border-t text-sm">
                <span className="text-muted-foreground">
                  Página {page + 1} de {Math.max(1, Math.ceil(recipientsTotal / pageSize))} — {recipientsTotal} {recipientsTotal === 1 ? "destinatário" : "destinatários"}
                </span>
                <div className="flex gap-2">
                  <Button
                    variant="outline" size="sm"
                    disabled={page === 0}
                    onClick={() => setPage((p) => Math.max(0, p - 1))}
                  >
                    <ChevronLeft className="h-3 w-3 mr-1" /> Anterior
                  </Button>
                  <Button
                    variant="outline" size="sm"
                    disabled={(page + 1) * pageSize >= recipientsTotal}
                    onClick={() => setPage((p) => p + 1)}
                  >
                    Próxima <ChevronRight className="h-3 w-3 ml-1" />
                  </Button>
                </div>
              </div>
            </>
          )}
        </Card>
      </div>

      <MessageDetailDialog
        messageId={detailMessageId}
        initialTab="report"
        onClose={() => setDetailMessageId(null)}
      />
    </DashboardLayout>
    </PermissionGuard>
  );
}
